import clsx from 'clsx'
import React, {FC, useState, useRef, useEffect, useCallback} from 'react'
import {Link} from 'react-router-dom'
import {KTSVG} from '../../../helpers'
import {HeaderUserMenu} from '../../../partials'
import {useLayout} from '../../core'

const toolbarButtonMarginClass = 'ms-1 ms-lg-2'
const btnClass = 'btn btn-icon btn-active-light-primary w-30px h-30px w-md-35px h-md-35px'

const RESERVATION_ITEMS = [
  {label: 'Yeni Rezervasyon', count: 0},
  {label: 'Web Rezervasyonu', count: 0},
  {label: 'Servis Rezervasyonu', count: 0},
  {label: 'Onaylı Rezervasyonlar', count: 0},
  {label: 'Mobil Teslimat', count: 0},
  {label: 'Mobil Ödeme', count: 0},
]

const REPORT_ITEMS = [
  {label: 'Tablet İşlemleri', icon: 'bi-tablet', path: '/reports/tablet'},
  {label: 'Çalışma Takvimi', icon: 'bi-calendar3', path: '/reports/calendar'},
  {label: 'Müsaitlik Raporu', icon: 'bi-bar-chart-line', path: '/reports/availability'},
  {label: 'Doluluk Raporu', icon: 'bi-graph-up', path: '/reports/occupancy'},
  {label: 'Ek Hizmet Raporu', icon: 'bi-person-badge', path: '/reports/extra-service'},
  {label: 'Gelir & Gider Raporu', icon: 'bi-bar-chart', path: '/reports/income-expense'},
  {label: 'Tahsilat Fatura Raporu', icon: 'bi-file-earmark-text', path: '/reports/invoice'},
  {label: 'Genel Rapor', icon: 'bi-bar-chart-fill', path: '/reports/general'},
  {label: 'Genel Tahsilat & Ödeme', icon: 'bi-graph-up-arrow', path: '/reports/payment'},
  {label: 'Müşteri Analiz', icon: 'bi-person-lines-fill', path: '/reports/customer-analysis'},
  {label: 'Rakip Fiyat Analiz', icon: 'bi-bar-chart-steps', path: '/reports/competitor'},
  {label: 'Genel Borç & Alacak Raporu', icon: 'bi-graph-down', path: '/reports/debt'},
  {label: 'Cari Hesap Extresi', icon: 'bi-receipt', path: '/reports/account-statement'},
  {label: 'Sigorta & Muayene Raporu', icon: 'bi-shield-check', path: '/reports/insurance'},
]

function useOutsideClick(ref: React.RefObject<HTMLElement>, onClose: () => void) {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [ref, onClose])
}

const PopupWrapper: FC<{
  open: boolean
  onClose: () => void
  width?: number
  align?: 'left' | 'right'
  children: React.ReactNode
}> = ({open, onClose, width = 280, align = 'right', children}) => {
  const ref = useRef<HTMLDivElement>(null)
  useOutsideClick(ref, onClose)
  if (!open) return null
  return (
    <div
      ref={ref}
      className='bg-white rounded shadow-lg border py-2'
      style={{
        position: 'absolute',
        top: '100%',
        ...(align === 'left' ? {left: 0} : {right: 0}),
        width,
        zIndex: 1050,
        maxHeight: '70vh',
        overflowY: 'auto',
      }}
    >
      {children}
    </div>
  )
}

const Topbar: FC = () => {
  const {config} = useLayout()
  const [clockOpen, setClockOpen] = useState(false)
  const [starOpen, setStarOpen] = useState(false)
  const [reportsOpen, setReportsOpen] = useState(false)
  const [plateSearch, setPlateSearch] = useState('')

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.()
    } else {
      document.exitFullscreen?.()
    }
  }, [])

  const closeAll = () => {
    setClockOpen(false)
    setStarOpen(false)
    setReportsOpen(false)
  }

  return (
    <div className='d-flex align-items-stretch flex-shrink-0 flex-grow-1'>
      {/* Ev / Home */}
      <div className={clsx('d-flex align-items-center', toolbarButtonMarginClass)}>
        <Link to='/dashboard' className={btnClass} title='Ana Sayfa'>
          <i className='bi bi-house-fill fs-3 text-dark' />
        </Link>
      </div>

      {/* Saat / Rezervasyon Popup */}
      <div className={clsx('d-flex align-items-center', toolbarButtonMarginClass)} style={{position: 'relative'}}>
        <div
          className={btnClass}
          title='Rezervasyonlar'
          onClick={() => {
            closeAll()
            setClockOpen((v) => !v)
          }}
        >
          <i className='bi bi-clock fs-4 text-warning' />
          <span
            className='position-absolute badge badge-circle badge-sm bg-danger text-white'
            style={{top: 2, right: 2, fontSize: 9, width: 16, height: 16, lineHeight: '16px'}}
          >
            0
          </span>
        </div>
        <PopupWrapper open={clockOpen} onClose={() => setClockOpen(false)} width={300} align='left'>
          {RESERVATION_ITEMS.map((item) => (
            <div
              key={item.label}
              className='d-flex justify-content-between align-items-center px-4 py-3 border-bottom'
            >
              <span className='fw-semibold text-dark fs-6'>{item.label}</span>
              <span className='text-muted fs-7'>{item.count} Adet</span>
            </div>
          ))}
        </PopupWrapper>
      </div>

      {/* Yıldız / Favori Menü */}
      <div className={clsx('d-flex align-items-center', toolbarButtonMarginClass)} style={{position: 'relative'}}>
        <div
          className={btnClass}
          title='Favori Menüler'
          onClick={() => {
            closeAll()
            setStarOpen((v) => !v)
          }}
        >
          <i className='bi bi-star-fill fs-4 text-warning' />
          <span
            className='position-absolute badge badge-circle badge-sm bg-danger text-white'
            style={{top: 2, right: 2, fontSize: 9, width: 16, height: 16, lineHeight: '16px'}}
          >
            0
          </span>
        </div>
        <PopupWrapper open={starOpen} onClose={() => setStarOpen(false)} width={300} align='left'>
          <div className='d-flex align-items-center justify-content-between px-4 py-3'>
            <span className='btn btn-sm btn-info d-flex align-items-center'>
              <span>Favori Menülerini Düzenle</span>
              <i className='bi bi-star-fill ms-2 text-white' />
            </span>
          </div>
        </PopupWrapper>
      </div>

      {/* Monitör */}
      <div className={clsx('d-flex align-items-center', toolbarButtonMarginClass)}>
        <Link to='/dashboard' className={btnClass} title='Monitör'>
          <i className='bi bi-display fs-4 text-dark' />
        </Link>
      </div>

      {/* Spacer */}
      <div className='flex-grow-1' />

      {/* Plaka Arama */}
      <div className={clsx('d-flex align-items-center', toolbarButtonMarginClass)}>
        <div className='d-flex align-items-center border rounded px-2' style={{height: 35}}>
          <input
            type='text'
            className='form-control form-control-sm border-0 bg-transparent ps-1'
            placeholder='Plaka Yazıp Enter Basınız'
            style={{width: 180, boxShadow: 'none'}}
            value={plateSearch}
            onChange={(e) => setPlateSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && plateSearch.trim()) {
                console.log('Plaka arama:', plateSearch)
              }
            }}
          />
          <i className='bi bi-search fs-5 text-dark cursor-pointer' />
        </div>
      </div>

      {/* Kullanıcı */}
      <div
        className={clsx('d-flex align-items-center', toolbarButtonMarginClass)}
        id='kt_header_user_menu_toggle'
        style={{position: 'relative'}}
      >
        <div
          className={btnClass}
          data-kt-menu-trigger='click'
          data-kt-menu-attach='parent'
          data-kt-menu-placement='bottom-end'
          data-kt-menu-flip='bottom'
          title='Kullanıcı'
        >
          <i className='bi bi-person-fill fs-3 text-dark' />
        </div>
        <HeaderUserMenu />
      </div>

      {/* Fullscreen (F11) */}
      <div className={clsx('d-flex align-items-center', toolbarButtonMarginClass)}>
        <div className={btnClass} title='Tam Ekran' onClick={toggleFullscreen}>
          <i className='bi bi-arrows-fullscreen fs-5 text-dark' />
        </div>
      </div>

      {/* Raporlar Grid */}
      <div className={clsx('d-flex align-items-center', toolbarButtonMarginClass)} style={{position: 'relative'}}>
        <div
          className={btnClass}
          title='Raporlar'
          onClick={() => {
            closeAll()
            setReportsOpen((v) => !v)
          }}
        >
          <i className='bi bi-grid-3x3-gap-fill fs-4 text-dark' />
        </div>
        <PopupWrapper open={reportsOpen} onClose={() => setReportsOpen(false)} width={320}>
          {REPORT_ITEMS.map((item) => (
            <Link
              key={item.label}
              to={item.path}
              className='d-flex align-items-center px-4 py-3 border-bottom text-dark text-hover-primary'
              onClick={() => setReportsOpen(false)}
              style={{textDecoration: 'none'}}
            >
              <i className={`bi ${item.icon} fs-4 me-3 text-info`} />
              <span className='fw-semibold fs-6'>{item.label}</span>
            </Link>
          ))}
        </PopupWrapper>
      </div>

      {/* Aside Toggler (mobile) */}
      {config.header.left === 'menu' && (
        <div className='d-flex align-items-center d-lg-none ms-2 me-n3' title='Show header menu'>
          <div
            className='btn btn-icon btn-active-light-primary w-30px h-30px w-md-40px h-md-40px'
            id='kt_header_menu_mobile_toggle'
          >
            <KTSVG path='/media/icons/duotune/text/txt001.svg' className='svg-icon-1' />
          </div>
        </div>
      )}
    </div>
  )
}

export {Topbar}
